export type SortValue = string | number | null;

const sortCollator = new Intl.Collator('sv-SE', { sensitivity: 'base' });

// Longer digit strings, such as OCR or reference numbers, would lose precision as a number,
// so they stay text and are compared digit by digit.
const numericStringPattern = /^\s*-?\d{1,15}(\.\d+)?\s*$/;

const numberChunkPattern = /(\d+(?:\.\d+)?)/;

/**
 * Normalises a cell's value for sorting: numbers and numeric strings (e.g. Postgres decimals
 * such as "1234.50") become numbers, empty values become null, everything else stays text.
 */
export const toSortValue = (value: unknown): SortValue => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') return numericStringPattern.test(value) ? Number(value) : value;
  return String(value);
};

/** Compares two unsigned decimal strings exactly, whatever their length. */
const compareNumberChunks = (a: string, b: string): number => {
  const [aInteger, aFraction = ''] = a.split('.');
  const [bInteger, bFraction = ''] = b.split('.');
  const aWhole = aInteger.replace(/^0+/, '');
  const bWhole = bInteger.replace(/^0+/, '');

  if (aWhole.length !== bWhole.length) return aWhole.length - bWhole.length;
  if (aWhole !== bWhole) return aWhole < bWhole ? -1 : 1;

  const aDecimals = aFraction.replace(/0+$/, '');
  const bDecimals = bFraction.replace(/0+$/, '');
  if (aDecimals === bDecimals) return 0;
  return aDecimals < bDecimals ? -1 : 1;
};

/**
 * Compares text in Swedish order, reading the numbers inside it as numbers, decimals included:
 * "A-9" before "A-10", and "12.25 kr" before "12.5 kr".
 */
const compareText = (a: string, b: string): number => {
  // Splitting on a capture group puts the text between numbers at even indexes and the numbers
  // at odd indexes, in both strings.
  const aParts = a.split(numberChunkPattern);
  const bParts = b.split(numberChunkPattern);
  const length = Math.min(aParts.length, bParts.length);

  for (let index = 0; index < length; index++) {
    const result =
      index % 2 === 1
        ? compareNumberChunks(aParts[index], bParts[index])
        : sortCollator.compare(aParts[index], bParts[index]);
    if (result !== 0) return result;
  }
  return aParts.length - bParts.length;
};

/**
 * Compares two sort values: numbers numerically, text with `compareText`, and empty values
 * last, which is where TanStack Table puts cells whose value is undefined.
 */
export const compareSortValues = (a: SortValue, b: SortValue): number => {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return compareText(String(a), String(b));
};
